/**
 * Bind an event handler
 *
 * @param {object} elem Event target
 * @param {string|array|object} type Event type
 * @param {function} fn Event handler
 */
export function on (elem, type, fn) {
  if (Array.isArray(type)) {
    for (let i = 0, l = type.length; i < l; i++) {
      on(elem, type[i], fn);
    }
  } else if (elem.addEventListener) {
    // If elem is a DOM element then use it's built-in event dispatcher
    return elem.addEventListener(type, fn);
  } else if (elem.on) {
    // bind a listener to a custom event target
    return elem.on(type, fn);
  }
}

/**
 * Bind an event handler that triggers only once
 *
 * @param {object} elem Event target
 * @param {string|array|object} type Event type
 * @param {function} fn Event handler
 */
export function once (elem, type, fn) {
  if (Array.isArray(type)) {
    for (let i = 0, l = type.length; i < l; i++) {
      once(elem, type[i], fn);
    }
  } else {
    // Create a wrapper callback function
    const callback = function (event) {
      // Unbind this handler as soon as it fires
      off(elem, type, callback);

      // Call the original event handler
      fn.call(this, event);
    };

    on(elem, type, callback);
  }
}

/**
 * Unbind an event handler
 *
 * @param {object} elem Event target
 * @param {string|array|object} type Event type
 * @param {function} fn Event handler
 */
export function off (elem, type, fn) {
  // if just the element was passed then attempt to remove ALL event listeners
  // as long as the element is a custom event emitter
  if (arguments.length === 1 && elem.off) {
    elem._events = {};
  } else if (Array.isArray(type)) {
    // If the second argument passed is an array, then assume an array of event types
    // and unbind the callback from all types in the list on the element
    for (let i = 0, l = type.length; i < l; i++) {
      off(elem, type[i], fn);
    }
  } else if (elem.removeEventListener) {
    // If elem is a DOM element then use it's built-in event dispatcher
    return elem.removeEventListener(type, fn);
  } else if (elem.off) {
    // remove listeners from custom event targets
    return elem.off(type, fn);
  }
}

/**
 * Trigger an event on the target
 *
 * @todo allow for hinting that an event should be deferred till the next call frame before triggering
 *
 * @param {object} elem Event target
 * @param {object} event Event object
 *
 * @return {boolean}
 */
export function trigger (elem, event) {
  // If elem is a DOM element then use it's built-in event dispatcher
  if (elem.dispatchEvent) {
    return elem.dispatchEvent(event);
  } else if (elem.trigger) {
    // trigger on a custom event target
    return elem.trigger(event);
  } else if (elem.emit) {
    return elem.emit(event);
  }
}

/**
 * Redispatches an event on an event target
 *
 * @param {Element|Object} elem   The redispatched event target
 * @param {Event|Object}   event  Event to be redispatched on the event target
 * @param {String}         prefix Optional prefix to apply to the redispatched event type
 *
 * @return {Event|Object}
 */
export function redispatch (elem, event, prefix = '') {
  const cloned = Object.create(event);
  const _type = `${prefix}${event.type}`;
  const _event = (event instanceof window.Event)
    ? new event.constructor(_type, cloned)
    : Object.assign(cloned, { type: _type });

  Object.defineProperty(_event, 'originalEvent', {
    get () { return event; }
  });

  return trigger(elem, _event);
}

/**
 * Cancel an event
 *
 * @param {Element|Object} elem  The event target
 * @param {Event|Object}   event The event to be cancelled
 */
export function cancel (elem, event) {
  event.cancelBubble = true;
  event.isImmediatePropagationStopped = function () {
    return true;
  };
  event.isPropagationStopped = function () {
    return true;
  };
}

/**
 * Redispatch an event with a prefix
 *
 * @param {Element|Object} elem  The event target
 * @param {Event|Object}   event The original event to be prefixed
 *
 * @return {Event|Object}
 */
export function prefix (elem, event) {
  cancel(elem, event);
  return redispatch(elem, event);
}

export default {
  off,
  on,
  once,
  redispatch,
  prefix,
  cancel,
  trigger
};
